import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Home, LayoutDashboard, LogOut, ChevronDown } from 'lucide-react';
import { api } from '@/api';
import { getStoredToken, getStoredUser, setStoredUser, initAuth, setAuthToken } from '@/utils/auth';

// Import split admin components
import { ResetPassword, AdminAuth, VerifyEmail } from './AdminAuth';
import { TeamNameAutocomplete, MediaPickerDialog } from './AdminShared';
import { AdminDashboard } from './AdminDashboard';
import { AdminPartners, AdminPageBanners, AdminLeagues, AdminTeams } from './AdminSports';
import { AdminGames, AdminFixtures } from './AdminGamesSection';
import { AdminMediaLibrary, AdminNews, AdminPodcast } from './AdminContent';
import { AdminAdvertising, AdminSiteSettings } from './AdminAds';
import { AdminClubsManager } from './AdminClubs';
import { MatchGraphicGenerator } from './MatchGraphicGenerator';
import { WeeklyScheduleGenerator } from './WeeklyScheduleGenerator';
import BulkImportManager from './BulkImportManager';
import AdminBookings from './AdminBookings';
import AdminChannels from './AdminChannels';
import AdminAnalytics from './AdminAnalytics';
import AdminPlans from './AdminPlans';
import PullLinks from './PullLinks';
import { AdminEnforcement } from './AdminEnforcement';

const TAB_ITEMS = [
  { value: 'dashboard', label: 'Dashboard', color: 'sky' },
  { value: 'leagues', label: 'Leagues', color: 'sky' },
  { value: 'teams', label: 'Teams', color: 'sky' },
  { value: 'games', label: 'Games', color: 'sky' },
  { value: 'fixtures', label: 'Fixtures', color: 'sky' },
  { value: 'news', label: 'News', color: 'sky' },
  { value: 'podcast', label: 'Podcast', color: 'sky' },
  { value: 'graphics', label: 'Graphics', color: 'sky' },
  { value: 'clubs', label: 'Clubs', color: 'sky' },
  { value: 'partners', label: 'Partners', color: 'sky' },
  { value: 'banners', label: 'Banners', color: 'sky' },
  { value: 'media', label: 'Media', color: 'sky' },
  { value: 'advertising', label: 'Advertising', color: 'green' },
  { value: 'bulk-import', label: 'Bulk Import', color: 'amber' },
  { value: 'channels', label: 'Channels', color: 'red' },
  { value: 'analytics', label: 'Analytics', color: 'violet' },
  { value: 'plans', label: 'Plans', color: 'orange' },
  { value: 'bookings-mgmt', label: 'Bookings', color: 'emerald' },
  { value: 'enforcement', label: 'Enforcement', color: 'red' },
  { value: 'settings', label: 'Settings', color: 'sky' },
];

const AdminPanel = ({ sports }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [stats, setStats] = useState({});
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [partners, setPartners] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('token') && location.pathname === '/verify') {
      return;
    }
  }, [location]);

  useEffect(() => {
    const checkAuth = async () => {
      const token = getStoredToken();
      const user = getStoredUser();
      if (token && user) {
        initAuth();
        try {
          await api.getMe();
          setIsAuthenticated(true);
          setCurrentUser(user);
        } catch (error) {
          setAuthToken(null);
          setStoredUser(null);
        }
      }
      setCheckingAuth(false);
    };
    checkAuth();
  }, []);

  const handleLogin = (user) => {
    setIsAuthenticated(true);
    setCurrentUser(user);
  };

  const handleLogout = () => {
    setAuthToken(null);
    setStoredUser(null);
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  const loadAllData = useCallback(async () => {
    try {
      const [statsRes, leaguesRes, teamsRes, gamesRes, fixturesRes, partnersRes] = await Promise.all([
        api.getStats(),
        api.getLeagues(),
        api.getTeams(),
        api.getGames({ limit: 100 }),
        api.getFixtures({ limit: 100 }),
        api.getPartners(null)
      ]);
      setStats(statsRes.data);
      setLeagues(leaguesRes.data);
      setTeams(teamsRes.data);
      setGames(gamesRes.data);
      setFixtures(fixturesRes.data);
      setPartners(partnersRes.data);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadAllData();
    }
  }, [isAuthenticated, loadAllData]);

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="animate-spin w-12 h-12 border-4 border-sky-400 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminAuth onLogin={handleLogin} />;
  }

  const activeLabel = TAB_ITEMS.find(t => t.value === activeTab)?.label || 'Dashboard';

  return (
    <div className="min-h-screen py-4 sm:py-8 px-3 sm:px-4" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto">
        {/* Header — stacks on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-8">
          <div>
            <h1 className="text-xl sm:text-3xl font-bold text-white flex items-center gap-2">
              <LayoutDashboard className="w-5 h-5 sm:w-8 sm:h-8" /> Admin Panel
            </h1>
            <p className="text-gray-400 text-sm">Welcome, {currentUser?.name}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="border-sky-400 text-sky-100 text-xs sm:text-sm" onClick={() => navigate('/')}>
              <Home className="w-3.5 h-3.5 mr-1.5" /> Back to Site
            </Button>
            <Button variant="outline" size="sm" className="border-red-400 text-red-300 hover:bg-red-500/20 text-xs sm:text-sm" onClick={handleLogout}>
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Logout
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setShowMobileNav(false); }} className="w-full">
          {/* Mobile: dropdown-style nav */}
          <div className="sm:hidden mb-4">
            <button
              onClick={() => setShowMobileNav(!showMobileNav)}
              className="w-full flex items-center justify-between bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm font-medium"
            >
              <span>{activeLabel}</span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showMobileNav ? 'rotate-180' : ''}`} />
            </button>
            {showMobileNav && (
              <div className="mt-1 bg-gray-800 border border-gray-700 rounded-lg overflow-hidden max-h-[60vh] overflow-y-auto">
                {TAB_ITEMS.map(tab => (
                  <button
                    key={tab.value}
                    onClick={() => { setActiveTab(tab.value); setShowMobileNav(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                      activeTab === tab.value
                        ? 'bg-sky-500/20 text-sky-300 font-medium'
                        : 'text-gray-300 hover:bg-gray-700/50'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Desktop: scrollable tab strip */}
          <TabsList className="hidden sm:flex flex-wrap gap-1 bg-gray-800/50 mb-6 p-2">
            {TAB_ITEMS.map(tab => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className={`data-[state=active]:bg-${tab.color}-300 text-xs px-3 py-1.5`}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="dashboard">
            <AdminDashboard stats={stats} sports={sports} leagues={leagues} onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="leagues">
            <AdminLeagues leagues={leagues} sports={sports} onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="teams">
            <AdminTeams teams={teams} leagues={leagues} sports={sports} onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="games">
            <AdminGames games={games} teams={teams} leagues={leagues} sports={sports} onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="fixtures">
            <AdminFixtures fixtures={fixtures} leagues={leagues} sports={sports} onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="news">
            <AdminNews leagues={leagues} teams={teams} onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="graphics">
            <Tabs defaultValue="match" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="match">Match Graphic</TabsTrigger>
                <TabsTrigger value="schedule">Weekly Schedule</TabsTrigger>
              </TabsList>
              <TabsContent value="match">
                <MatchGraphicGenerator games={games} leagues={leagues} />
              </TabsContent>
              <TabsContent value="schedule">
                <WeeklyScheduleGenerator />
              </TabsContent>
            </Tabs>
          </TabsContent>
          <TabsContent value="podcast">
            <AdminPodcast onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="clubs">
            <AdminClubsManager leagues={leagues} />
          </TabsContent>
          <TabsContent value="partners">
            <AdminPartners partners={partners} onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="banners">
            <AdminPageBanners onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="media">
            <AdminMediaLibrary />
          </TabsContent>
          <TabsContent value="advertising">
            <AdminAdvertising />
          </TabsContent>
          <TabsContent value="bulk-import">
            <BulkImportManager onRefresh={loadAllData} />
          </TabsContent>
          <TabsContent value="channels">
            <Tabs defaultValue="manage" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="manage">Manage Channels</TabsTrigger>
                <TabsTrigger value="pull-links">Pull Links</TabsTrigger>
              </TabsList>
              <TabsContent value="manage">
                <AdminChannels games={games} onRefresh={loadAllData} />
              </TabsContent>
              <TabsContent value="pull-links">
                <PullLinks />
              </TabsContent>
            </Tabs>
          </TabsContent>
          <TabsContent value="analytics">
            <AdminAnalytics />
          </TabsContent>
          <TabsContent value="plans">
            <AdminPlans />
          </TabsContent>
          <TabsContent value="bookings-mgmt">
            <AdminBookings />
          </TabsContent>
          <TabsContent value="enforcement">
            <AdminEnforcement />
          </TabsContent>
          <TabsContent value="settings">
            <AdminSiteSettings />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export { TeamNameAutocomplete, MediaPickerDialog, AdminPanel, AdminAuth, VerifyEmail, ResetPassword };
